import {
  ContextualSaveBar as ContextualSaveBarPolaris,
  ContextualSaveBarProps,
} from "@shopify/polaris"
import { cloneDeep, isEqual, merge } from "lodash-es"
import { emitter, useCallbackWithLoading } from "polaris-enhance"
import { useCallback, useEffect, useMemo, useState } from "react"
import { Prompt, useHistory } from "react-router-dom"

import { showConfirmModalBeforeLeave } from "@/components/ConfirmModal/ConfirmModalInPage"
import { isShopifyEmbedded } from "@/utils/app-bridge"

interface useSaveBarProps {
  onSave(): any | Promise<any>
  onDiscard?(): any | Promise<any>
  saving: boolean
  isChanged: boolean
  confirmDisabled?: boolean
  isWithFullscreen?: boolean
  backurl?: string
}
/**
 * 嵌入shopify时候使用的saveBar
 * @param props useSaveBarProps
 */
function useEmbedSaveBar(props: useSaveBarProps) {
  useEffect(() => {
    emitter.emit("platform:saveBar:change", {
      open: props.isChanged,
      saving: props.saving,
      onSave: props.onSave,
      onDiscard: props.onDiscard,
      disabledConfirm: props.confirmDisabled,
    })
  }, [props])
}

export function useSaveBar(props: useSaveBarProps) {
  useEmbedSaveBar(props)
  return {
    node: null,
  }
}

interface useUnsavedModalProps {
  isChanged: boolean
  discard: () => void
}
export function useUnsavedModal(props: useUnsavedModalProps) {
  const { isChanged, discard } = props
  const history = useHistory()

  return {
    promptDom: (
      <Prompt
        when={isChanged}
        message={(location) => {
          showConfirmModalBeforeLeave().then((res) => {
            if (res) {
              discard()
              setTimeout(() => {
                history.push(location)
              }, 50)
            }
          })
          return false
        }}
      />
    ),
  }
}

/**
 * 跟原useContextualSaveBarEnhance使用一致，只是兼容了shopify平台
 */
export function useContextualSaveBarCombine<T>(props: {
  confirmDisabled?: boolean | ((form: T) => boolean)
  discardChange?: () => void
  initValue: T
  submit: (form: T, copiedData: T) => Promise<boolean>
  request?: (form?: T) => Promise<T>
  isQueryImmediately?: boolean
}) {
  const {
    initValue,
    submit,
    request,
    isQueryImmediately = true,
    confirmDisabled,
    discardChange,
  } = props
  const [form, setForm] = useState(initValue)
  const [copiedData, setCopiedData] = useState(cloneDeep(initValue))
  const [updateFlag, setUpdateFlag] = useState(false)
  const [resetKey, setResetKey] = useState(0)

  useEffect(() => {
    if (updateFlag) {
      setUpdateFlag(false)
      setCopiedData(cloneDeep(form))
    }
  }, [updateFlag, form])

  const update = useCallback((copied?: T) => {
    if (copied) {
      setCopiedData(cloneDeep(copied))
    } else {
      setUpdateFlag(true)
    }
  }, [])

  const isChanged = useMemo(() => {
    return !isEqual(copiedData, form)
  }, [copiedData, form])

  const [requesting, handleRequest] = useCallbackWithLoading(
    async (_from) => {
      const data = await request?.(_from)
      if (data) {
        setForm(cloneDeep(data))
        update(cloneDeep(data))
        setResetKey((prev) => prev + 1)
      }
    },
    [form, update],
    true,
  )

  const [saving, handleSave] = useCallbackWithLoading(async () => {
    const result = await submit(form, copiedData)
    if (result === true) {
      update()
      setResetKey((prev) => prev + 1)
    }
  }, [submit, form, copiedData, update])

  useEffect(() => {
    isQueryImmediately && handleRequest()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const isEmbed = isShopifyEmbedded()
  const discard = () => {
    setForm(cloneDeep(copiedData))
    setResetKey((prev) => prev + 1)
    discardChange?.()
  }

  const save = async () => {
    await handleSave()
  }

  const disabled =
    typeof confirmDisabled === "function"
      ? confirmDisabled(form)
      : confirmDisabled

  useEmbedSaveBar({
    isChanged,
    saving,
    onDiscard: discard,
    onSave: save,
    confirmDisabled: disabled,
  })

  const { promptDom } = useUnsavedModal({
    isChanged,
    discard,
  })

  const renderSaveBar = (renderProps?: ContextualSaveBarProps) => {
    const defaultProps: ContextualSaveBarProps = {
      alignContentFlush: true,
      fullWidth: true,
      message: t("UnsavedChanges0"),
      saveAction: {
        content: t("Save"),
        loading: saving,
        onAction: handleSave,
        disabled: disabled,
      },
      discardAction: {
        onAction: discard,
      },
    }

    const props = merge(defaultProps, renderProps)
    return !isEmbed && isChanged && <ContextualSaveBarPolaris {...props} />
  }

  return {
    renderSaveBar: () => {
      return (
        <>
          {renderSaveBar()}
          {promptDom}
        </>
      )
    },
    requesting,
    isChanged,
    form,
    setForm,
    saving,
    copiedData,
    resetKey,
    refresh: handleRequest,
    handleSave,
  }
}
